import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Lock, Save, AlertCircle } from "lucide-react";
import { EMPTY_AVOCAT, EMPTY_ADRESSE, EMPTY_MEGA } from "./avocat/constants";
import { IdentificationTab } from "./avocat/IdentificationTab";
import { AdressesTab } from "./avocat/AdressesTab";
import { InhabTab } from "./avocat/InhabTab";
import { MegaTab } from "./avocat/MegaTab";
import { WebTab } from "./avocat/WebTab";
import { HistoriqueTab } from "./avocat/HistoriqueTab";
import { isValidNAS } from "./avocat/formatters";

const TAB_CLASS = `
    relative inline-flex items-center justify-center
    h-11 px-4 py-2 rounded-md rounded-b-none
    text-xs font-bold uppercase tracking-wider
    border-b-[3px] border-transparent
    text-slate-600 bg-slate-50/50 hover:bg-slate-100 hover:text-slate-900
    transition-all
    data-[state=active]:bg-white
    data-[state=active]:text-[#0033A0]
    data-[state=active]:border-b-[#0033A0]
    data-[state=active]:shadow-sm
    data-[state=active]:-mb-[2px]
    disabled:text-slate-300 disabled:bg-slate-50 disabled:cursor-not-allowed
    disabled:hover:bg-slate-50 disabled:hover:text-slate-300
    disabled:border-b-transparent disabled:opacity-100
`;

// Champs forcés à `false` avant envoi pour les avocats permanents (type A).
// NOTE : `mega` et `payable` sont désormais autorisés pour les A (voir
// IdentificationTab — verrous levés). Ils ne sont donc plus forcés ici.
const PERMANENT_FORCED_FALSE = ["factweb", "confweb", "depodirect", "surveil"];

// Champs RETIRÉS du payload PUT /avocats/{id}. Notamment `adresse` est géré
// par les endpoints dédiés /adresses/{id} — l'inclure provoquerait l'écrasement
// de l'adresse courante avec des champs vides si le form était désynchronisé.
const FORM_EXCLUDED_FROM_PAYLOAD = ["id", "code", "created_at", "updated_at", "usermodif",
    "motpasse1_set", "motpasse2_set", "adresse", "dateinscbarr"];

const buildSafePayload = (form) => {
    const isPermanent = (form.type_code === "A")
        || (form.code || "").toUpperCase().startsWith("A");
    const out = { ...form };
    FORM_EXCLUDED_FROM_PAYLOAD.forEach((k) => delete out[k]);
    const nasDigits = String(out.nas || "").replace(/\D/g, "");
    if (nasDigits.length > 0 && !isValidNAS(nasDigits)) {
        delete out.nas;
    } else if (nasDigits.length > 0) {
        out.nas = nasDigits;
    }
    if (isPermanent) {
        PERMANENT_FORCED_FALSE.forEach((k) => { out[k] = false; });
    }
    return out;
};

// Sérialisation stable pour comparer les baselines aux valeurs courantes
// (détection « dirty »). On clone via JSON pour normaliser ordre des clés et
// types — suffisant pour notre usage (pas de Map/Set/Date).
const sig = (v) => JSON.stringify(v ?? null);

export default function AvocatSheet({ open, onOpenChange, avocat, onSaved }) {
    const { user } = useAuth();
    const readOnly = user?.role === "lecteur";
    const isAdmin = user?.role === "admin";
    const isEditing = !!(avocat && avocat.id);
    const avocatId = avocat?.id;

    // ============================================================
    //   ÉTATS (form + sections liées)
    // ============================================================
    const [form, setForm] = useState(EMPTY_AVOCAT);
    const [creating, setCreating] = useState(false);
    const [saving, setSaving] = useState(false);

    const [adresses, setAdresses] = useState([]);
    const [adressesLoaded, setAdressesLoaded] = useState(false);
    const [editAdr, setEditAdr] = useState(null);

    const [inhabs, setInhabs] = useState([]);
    const [editInhab, setEditInhab] = useState(null);

    const [mega, setMega] = useState(EMPTY_MEGA);
    const [taxes, setTaxes] = useState({ tps: "", tvq: "", firme: "", found: false, loading: false });
    const [activeTab, setActiveTab] = useState("ident");

    // Adresse courriel éditable dans l'onglet Identification
    const courantAdresse = adresses.find((a) => a.courant) || null;
    const [currentEmailDraft, setCurrentEmailDraft] = useState("");

    // Flags qui débloquent les onglets Méga/Web (alignés avec ce qui est persisté côté serveur)
    const [savedMega, setSavedMega] = useState(false);
    const [savedFactweb, setSavedFactweb] = useState(false);

    // ============================================================
    //   BASELINES (= snapshots de la valeur "propre" persistée)
    //   Servent à détecter si une section est "dirty" pour activer
    //   le bouton Sauvegarder + l'indicateur en haut.
    // ============================================================
    const [formBaseline, setFormBaseline] = useState(null);
    const [editAdrBaseline, setEditAdrBaseline] = useState(null);
    const [megaBaseline, setMegaBaseline] = useState(null);
    const [emailBaseline, setEmailBaseline] = useState("");

    // === État "dirty" calculé ===
    // form : on ignore les champs gérés serveur dans la comparaison
    const formForCompare = (() => {
        if (!form) return null;
        const { code: _c, updated_at: _u, usermodif: _m, ...rest } = form;
        return rest;
    })();
    const baselineForCompare = (() => {
        if (!formBaseline) return null;
        const { code: _c, updated_at: _u, usermodif: _m, ...rest } = formBaseline;
        return rest;
    })();
    const formDirty = !!formBaseline && sig(formForCompare) !== sig(baselineForCompare);

    const editAdrDirty = !!editAdr && sig(editAdr) !== sig(editAdrBaseline);
    const megaDirty = !!megaBaseline && sig(mega) !== sig(megaBaseline);
    const emailDirty = !!courantAdresse && (currentEmailDraft || "") !== (emailBaseline || "");
    // Le bouton "Sauvegarder" global N'INCLUT PAS l'adresse en cours d'édition :
    // les adresses sont une entité 1-à-N gérée via leur propre bouton dans
    // l'onglet Adresses (pour éviter la confusion quand on en a plusieurs).
    const anyDirty = formDirty || megaDirty || emailDirty;

    // ============================================================
    //   Reset baseline quand un AVOCAT change (ouverture / switch)
    // ============================================================
    // On track le `id` du dernier avocat *chargé* pour éviter de recharger
    // inutilement lorsque le parent re-passe la même fiche (ex. après save).
    // Pour le mode CRÉATION (avocat truthy mais sans `id`), on reset à
    // chaque nouveau objet `{}` passé par le parent — sinon une transition
    // « avocat existant → fermer → Nouvel avocat » garderait les valeurs
    // chargées du précédent (lastRef et currentId tous deux non synchronisés).
    const lastAvocatIdRef = useRef(null);
    useEffect(() => {
        if (!avocat) return; // sheet fermée — pas de reset
        setActiveTab("ident");
        if (avocat.id) {
            if (avocat.id === lastAvocatIdRef.current) return; // déjà chargé
            lastAvocatIdRef.current = avocat.id;
            const next = { ...EMPTY_AVOCAT, ...avocat, adresse: { ...EMPTY_AVOCAT.adresse, ...(avocat.adresse || {}) } };
            setForm(next);
            setFormBaseline(next);
            setSavedMega(!!avocat.mega);
            setSavedFactweb(!!avocat.factweb);
        } else {
            // Mode création : on force toujours un état VIERGE — chaque clic sur
            // « Nouvel avocat » passe une nouvelle référence `{}` au composant.
            lastAvocatIdRef.current = null;
            setForm(EMPTY_AVOCAT);
            setFormBaseline(null);
            setSavedMega(false);
            setSavedFactweb(false);
        }
        // Reset des autres baselines/sections liées (seront re-set par les
        // useEffect de chargement quand l'avocat aura un id).
        setEditAdr(null);
        setEditAdrBaseline(null);
        setMegaBaseline(null);
        setEmailBaseline("");
        setCurrentEmailDraft("");
    }, [avocat]);

    // ============================================================
    //   Chargement des données liées à l'avocat
    // ============================================================
    useEffect(() => {
        if (!avocatId) {
            setAdresses([]);
            setAdressesLoaded(false);
            setInhabs([]);
            setMega(EMPTY_MEGA);
            setMegaBaseline(null);
            setTaxes({ tps: "", tvq: "", firme: "", found: false, loading: false });
            return;
        }
        setAdressesLoaded(false);
        api.get(`/avocats/${avocatId}/adresses`)
            .then(({ data }) => setAdresses(data || []))
            .catch(() => setAdresses([]))
            .finally(() => setAdressesLoaded(true));
        api.get(`/avocats/${avocatId}/inhabilites`).then(({ data }) => setInhabs(data || [])).catch(() => setInhabs([]));
        api.get(`/avocats/${avocatId}/mega`).then(({ data }) => {
            const next = data && Object.keys(data).length > 0 ? { ...EMPTY_MEGA, ...data } : EMPTY_MEGA;
            setMega(next);
            setMegaBaseline(next);
        }).catch(() => {
            setMega(EMPTY_MEGA);
            setMegaBaseline(EMPTY_MEGA);
        });
        setTaxes((t) => ({ ...t, loading: true }));
        api.get(`/avocats/${avocatId}/taxes`)
            .then(({ data }) => setTaxes({
                tps: data?.tps || "", tvq: data?.tvq || "",
                firme: data?.firme || "", found: !!data?.found, loading: false,
            }))
            .catch(() => setTaxes({ tps: "", tvq: "", firme: "", found: false, loading: false }));
    }, [avocatId]);

    // Sync brouillon email + baseline quand l'adresse courante change (load, save adresse)
    useEffect(() => {
        const serverEmail = courantAdresse?.email || "";
        setCurrentEmailDraft(serverEmail);
        setEmailBaseline(serverEmail);
    }, [courantAdresse?.id, courantAdresse?.email]);

    // Onglet Adresses : panneau d'édition toujours visible — auto-init
    useEffect(() => {
        if (!isEditing) return;
        if (!adressesLoaded) return;
        if (adresses.length === 0) {
            if (!editAdr) {
                const empty = { ...EMPTY_ADRESSE, courant: true };
                setEditAdr(empty);
                setEditAdrBaseline(empty);
            }
            return;
        }
        const isNewMode = editAdr && !editAdr.id;
        if (isNewMode) return;
        const stillExists = editAdr?.id && adresses.some((a) => a.id === editAdr.id);
        if (!stillExists) {
            const fallback = adresses.find((a) => a.courant) || adresses[0];
            setEditAdr(fallback);
            setEditAdrBaseline(fallback);
        }
    }, [adresses, adressesLoaded, editAdr, isEditing]);

    // Si l'avocat persisté n'a plus le flag Méga/Web → rebascule sur Ident
    useEffect(() => {
        if (activeTab === "mega" && !savedMega) setActiveTab("ident");
        if (activeTab === "web" && !savedFactweb) setActiveTab("ident");
    }, [savedMega, savedFactweb, activeTab]);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const onTypeChange = (t) => {
        setForm((f) => {
            const next = { ...f, type_code: t };
            if (t === "A") {
                PERMANENT_FORCED_FALSE.forEach((k) => { next[k] = false; });
            }
            return next;
        });
    };

    // ============================================================
    //   FONCTIONS DE SAUVEGARDE (manuelles, déclenchées par le
    //   bouton « Sauvegarder » du header ou par les actions
    //   spécifiques de certains onglets — ex. Inhab garde son
    //   propre flux create/edit/delete inline).
    // ============================================================
    const saveForm = async () => {
        const payload = buildSafePayload(form);
        const { data } = await api.put(`/avocats/${avocatId}`, payload);
        setSavedMega(!!data?.mega);
        setSavedFactweb(!!data?.factweb);
        const next = { ...form, code: data.code ?? form.code,
            updated_at: data.updated_at ?? form.updated_at,
            usermodif: data.usermodif ?? form.usermodif };
        setForm(next);
        setFormBaseline(next);
        onSaved?.({ updatedAvocat: data, silent: true });
    };

    const saveAdresseRow = async (rowOverride = null) => {
        const data = rowOverride || editAdr;
        if (!data) return;
        const { id, courant, ...payload } = data;
        let savedId = id;
        let pendingEditAdr = null;
        if (id) {
            await api.put(`/avocats/${avocatId}/adresses/${id}?courant=${!!courant}`, payload);
        } else {
            const { data: created } = await api.post(`/avocats/${avocatId}/adresses?courant=${!!courant}`, payload);
            savedId = created?.id;
            if (savedId) pendingEditAdr = { ...data, id: savedId };
        }
        const { data: list } = await api.get(`/avocats/${avocatId}/adresses`);
        setAdresses(list || []);
        const finalEditAdr = pendingEditAdr || (list || []).find((a) => a.id === id) || data;
        setEditAdr(finalEditAdr);
        setEditAdrBaseline(finalEditAdr);
        onSaved?.({ close: false, silent: true });
        return savedId;
    };

    const saveMegaSection = async () => {
        await api.put(`/avocats/${avocatId}/mega`, mega);
        setMegaBaseline(mega);
        onSaved?.({ silent: true });
    };

    const saveEmailSection = async () => {
        if (!courantAdresse) return;
        const adr = courantAdresse;
        const payload = {
            address: adr.address || "", adresse2: adr.adresse2 || "",
            adresse3: adr.adresse3 || "", ville: adr.ville || "",
            province: adr.province || "", codepostal: adr.codepostal || "",
            telephone: adr.telephone || "", telephone2: adr.telephone2 || "",
            poste1: adr.poste1 || "", poste2: adr.poste2 || "",
            fax: adr.fax || "", email: (currentEmailDraft || "").trim(),
        };
        await api.put(`/avocats/${avocatId}/adresses/${adr.id}?courant=true`, payload);
        setAdresses((prev) => prev.map((a) =>
            a.id === adr.id ? { ...a, email: payload.email } : a));
        setEmailBaseline(payload.email);
        onSaved?.({ silent: true });
    };

    // === Bouton global « Sauvegarder » ===
    // Ne gère PAS l'adresse en cours d'édition — l'onglet Adresses a son
    // propre bouton (entité 1-à-N gérée indépendamment).
    const handleSaveAll = async () => {
        if (!anyDirty || saving) return;
        setSaving(true);
        try {
            if (formDirty) await saveForm();
            if (megaDirty) await saveMegaSection();
            if (emailDirty) await saveEmailSection();
            toast.success("Modifications enregistrées");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur d'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    // === Save dédié pour l'adresse en cours (déclenché par AdressesTab) ===
    const handleSaveAdresse = async () => {
        if (!editAdr || !editAdrDirty || saving) return;
        if (!isAdresseSavable(editAdr)) {
            toast.error("Veuillez compléter tous les champs requis (adresse, ville, code postal, courriel).");
            return;
        }
        setSaving(true);
        try {
            await saveAdresseRow();
            toast.success(editAdr.id ? "Adresse modifiée" : "Adresse créée");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur d'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    // ============================================================
    //   GESTION DE LA FERMETURE (confirme si dirty)
    // ============================================================
    const [pendingClose, setPendingClose] = useState(false);

    const handleSheetOpenChange = (newOpen) => {
        // Close confirmation : inclut l'adresse en cours d'édition même si
        // elle est sauvegardée séparément (sinon l'utilisateur peut perdre
        // ses modifs en fermant la fiche sans s'en rendre compte).
        const hasUnsaved = anyDirty || editAdrDirty;
        if (!newOpen && hasUnsaved && isEditing) {
            setPendingClose(true);
            return;
        }
        onOpenChange(newOpen);
    };

    const handleConfirmSaveAndClose = async () => {
        if (formDirty || megaDirty || emailDirty) {
            await handleSaveAll();
        }
        if (editAdrDirty && isAdresseSavable(editAdr)) {
            await handleSaveAdresse();
        }
        setPendingClose(false);
        onOpenChange(false);
    };
    const handleConfirmDiscardAndClose = () => {
        setPendingClose(false);
        onOpenChange(false);
    };

    // ============================================================
    //   CRÉATION D'AVOCAT (POST initial)
    // ============================================================
    const handleCreate = async () => {
        if (!form.nom || !form.prenom) {
            toast.error("Nom et prénom sont requis");
            return;
        }
        if (form.nas && !isValidNAS(form.nas)) {
            toast.error("Numéro d'assurance sociale invalide");
            return;
        }
        setCreating(true);
        try {
            const isPermanent = (form.type_code === "A")
                || (form.code || "").toUpperCase().startsWith("A");
            const safeForm = isPermanent
                ? { ...form, ...Object.fromEntries(PERMANENT_FORCED_FALSE.map((k) => [k, false])) }
                : form;
            const { data } = await api.post("/avocats", safeForm);
            toast.success("Avocat créé");
            onSaved?.({ updatedAvocat: data });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        } finally {
            setCreating(false);
        }
    };

    // ============================================================
    //   Sous-actions Inhab (conservent leur flux propre — déjà manuel)
    // ============================================================
    const reloadInhabs = async () => {
        const { data } = await api.get(`/avocats/${avocatId}/inhabilites`);
        setInhabs(data || []);
    };
    const saveInhab = async (override = null) => {
        const data = override || editInhab;
        if (!data?.datedeb) return;
        const { id, ...payload } = data;
        let savedId = id;
        if (id) {
            await api.put(`/avocats/${avocatId}/inhabilites/${id}`, payload);
        } else {
            const { data: created } = await api.post(`/avocats/${avocatId}/inhabilites`, payload);
            savedId = created?.id;
        }
        reloadInhabs();
        onSaved?.({ close: false, silent: true });
        return savedId;
    };
    const deleteInhab = async (item) => {
        if (!confirm("Supprimer cette période ?")) return;
        try {
            await api.delete(`/avocats/${avocatId}/inhabilites/${item.id}`);
            toast.success("Période supprimée");
            reloadInhabs();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    // === Suppression d'adresse (action dédiée — pas concernée par le save global) ===
    const deleteAdresse = async (adr) => {
        if (!confirm("Supprimer cette adresse ?")) return;
        try {
            await api.delete(`/avocats/${avocatId}/adresses/${adr.id}`);
            toast.success("Adresse supprimée");
            const { data } = await api.get(`/avocats/${avocatId}/adresses`);
            setAdresses(data || []);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
        }
    };

    // === Helper : switch d'adresse dans la liste (avec confirmation si dirty) ===
    const requestSelectAdresse = (adr) => {
        if (editAdrDirty) {
            const ok = window.confirm(
                "L'adresse en cours d'édition contient des modifications non enregistrées qui seront perdues. Continuer ?",
            );
            if (!ok) return;
        }
        setEditAdr(adr);
        setEditAdrBaseline(adr);
    };
    const requestNewAdresse = () => {
        if (editAdrDirty) {
            const ok = window.confirm(
                "L'adresse en cours d'édition contient des modifications non enregistrées qui seront perdues. Continuer ?",
            );
            if (!ok) return;
        }
        // Pré-remplissage à partir de l'adresse courante existante (le cas
        // échéant) — reprend uniquement les coordonnées de contact
        // (courriel, téléphones + postes, télécopieur) car elles suivent
        // généralement l'avocat, pas l'adresse postale.
        const courante = adresses.find((a) => a.courant);
        const prefill = courante ? {
            email: courante.email || "",
            telephone: courante.telephone || "",
            poste1: courante.poste1 || "",
            telephone2: courante.telephone2 || "",
            poste2: courante.poste2 || "",
            fax: courante.fax || "",
        } : {};
        const next = { ...EMPTY_ADRESSE, ...prefill, courant: adresses.length === 0 };
        setEditAdr(next);
        setEditAdrBaseline(next);
    };
    const cancelNewAdresse = () => {
        // Revient sur la courante (ou la 1re)
        const fallback = adresses.find((a) => a.courant) || adresses[0] || null;
        setEditAdr(fallback);
        setEditAdrBaseline(fallback);
    };

    const tabsCount = isAdmin ? 6 : 5;

    return (
        <Sheet open={open} onOpenChange={handleSheetOpenChange}>
            <SheetContent className="!w-screen !max-w-none sm:!max-w-none overflow-y-auto" data-testid="avocat-sheet">
                <div className="max-w-7xl mx-auto">
                    <SheetHeader>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <SheetTitle className="font-display text-2xl tracking-tight flex items-center gap-3 flex-wrap">
                                <span>{isEditing ? `${form.prenom} ${form.nom} (${form.code})` : "Nouvel avocat"}</span>
                                {isEditing && anyDirty && (
                                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1.5" data-testid="dirty-badge">
                                        <AlertCircle size={12} />
                                        Modifications non enregistrées
                                    </Badge>
                                )}
                            </SheetTitle>
                            {isEditing && !readOnly && (
                                <Button
                                    onClick={handleSaveAll}
                                    disabled={!anyDirty || saving}
                                    className="rounded-md bg-[#0033A0] hover:bg-[#002277] text-white disabled:bg-slate-200 disabled:text-slate-500"
                                    data-testid="save-all-btn"
                                >
                                    <Save size={14} className="mr-2" />
                                    {saving ? "Enregistrement…" : "Sauvegarder"}
                                </Button>
                            )}
                        </div>
                        <SheetDescription className="sr-only">
                            Fiche détaillée de l&apos;avocat — onglets Identification, Adresses,
                            Inhabilité, Méga, Web. Cliquez sur Sauvegarder pour enregistrer les modifications.
                        </SheetDescription>
                    </SheetHeader>

                    <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
                        <TabsList className={`grid w-full h-auto p-0 gap-1 bg-transparent border-b-2 border-slate-200 rounded-none ${tabsCount === 6 ? "grid-cols-6" : "grid-cols-5"}`}>
                            <TabsTrigger value="ident" data-testid="tab-ident" className={TAB_CLASS}>
                                Identification
                            </TabsTrigger>
                            <TabsTrigger value="adr" disabled={!isEditing} data-testid="tab-adr" className={TAB_CLASS}>
                                Adresses {adresses.length > 0 && (
                                    <span className="ml-1 text-[10px] font-bold bg-[#0033A0] text-white px-1.5 py-0.5 rounded-full">
                                        {adresses.length}
                                    </span>
                                )}
                            </TabsTrigger>
                            <TabsTrigger value="inhab" disabled={!isEditing} data-testid="tab-inhab" className={TAB_CLASS}>
                                Inhabilité {inhabs.length > 0 && (
                                    <span className="ml-1 text-[10px] font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded-full">
                                        {inhabs.length}
                                    </span>
                                )}
                            </TabsTrigger>
                            <TabsTrigger value="mega" disabled={!isEditing || !savedMega} data-testid="tab-mega" className={TAB_CLASS}>
                                Méga {!savedMega && isEditing && <Lock className="w-3 h-3 ml-1 opacity-60" />}
                            </TabsTrigger>
                            <TabsTrigger value="web" disabled={!isEditing || !savedFactweb} data-testid="tab-web" className={TAB_CLASS}>
                                Web {!savedFactweb && isEditing && <Lock className="w-3 h-3 ml-1 opacity-60" />}
                            </TabsTrigger>
                            {isAdmin && (
                                <TabsTrigger value="hist" disabled={!isEditing} data-testid="tab-hist" className={TAB_CLASS}>
                                    Historique
                                </TabsTrigger>
                            )}
                        </TabsList>

                        <TabsContent value="ident" className="mt-6">
                            <IdentificationTab
                                form={form} upd={upd} isEditing={isEditing} readOnly={readOnly}
                                creating={creating} onTypeChange={onTypeChange}
                                onCreate={handleCreate}
                                taxes={taxes}
                                currentEmail={currentEmailDraft}
                                onCurrentEmailChange={setCurrentEmailDraft}
                                hasCourantAdresse={!!courantAdresse}
                            />
                        </TabsContent>

                        <TabsContent value="adr" className="mt-6">
                            <AdressesTab
                                readOnly={readOnly} adresses={adresses}
                                editAdr={editAdr} setEditAdr={setEditAdr}
                                onDelete={deleteAdresse}
                                onSelectAdresse={requestSelectAdresse}
                                onNewAdresse={requestNewAdresse}
                                onCancelNew={cancelNewAdresse}
                                onSaveAdresse={handleSaveAdresse}
                                isDirty={editAdrDirty}
                                saving={saving}
                            />
                        </TabsContent>

                        <TabsContent value="inhab" className="mt-6">
                            <InhabTab
                                readOnly={readOnly} inhabs={inhabs}
                                editInhab={editInhab} setEditInhab={setEditInhab}
                                onSave={saveInhab} onDelete={deleteInhab}
                            />
                        </TabsContent>

                        <TabsContent value="mega" className="mt-6">
                            <MegaTab readOnly={readOnly} mega={mega} setMega={setMega} />
                        </TabsContent>

                        <TabsContent value="web" className="mt-6">
                            <WebTab
                                readOnly={readOnly} form={form} upd={upd}
                                avocatId={avocatId} avocat={avocat}
                                adresses={adresses}
                                onSaved={onSaved}
                            />
                        </TabsContent>

                        {isAdmin && (
                            <TabsContent value="hist" className="mt-6">
                                <HistoriqueTab avocatId={avocatId} />
                            </TabsContent>
                        )}
                    </Tabs>
                </div>
            </SheetContent>

            {/* Confirmation avant fermeture si modifications non sauvegardées */}
            <AlertDialog open={pendingClose} onOpenChange={setPendingClose}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Modifications non sauvegardées</AlertDialogTitle>
                        <AlertDialogDescription>
                            Vous avez des modifications en cours sur cette fiche. Voulez-vous les sauvegarder avant de fermer ?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel data-testid="close-cancel-btn">Annuler</AlertDialogCancel>
                        <Button
                            variant="outline"
                            onClick={handleConfirmDiscardAndClose}
                            data-testid="close-discard-btn"
                        >
                            Abandonner
                        </Button>
                        <AlertDialogAction
                            onClick={handleConfirmSaveAndClose}
                            className="bg-[#0033A0] hover:bg-[#002277]"
                            data-testid="close-save-btn"
                        >
                            Sauvegarder
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Sheet>
    );
}

// Validation locale d'adresse (utilisée par handleSaveAll pour ne pas POSTer
// une adresse vide). Duplique la logique d'AdressesTab pour rester découplé.
function isAdresseSavable(adr) {
    if (!adr) return false;
    if (!(adr.address || "").trim()) return false;
    if (!(adr.ville || "").trim()) return false;
    if (!(adr.codepostal || "").trim()) return false;
    if (!(adr.email || "").trim()) return false;
    return true;
}
